import { useEffect, useState } from "react";
import { driverStatisticsApi } from "api/driver";
import { useNotificationContext } from "contexts/NotificationContext";
import { IDriverStatisticsResponse } from "models/driverStatistics";
import moment from "moment";

export default function useDriverStatistics(date?: string, limit: number = 10) {
    const { showError } = useNotificationContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [statistics, setStatistics] = useState<IDriverStatisticsResponse["data"] | null>(null);
    const [responseDate, setResponseDate] = useState<string>("");

    useEffect(() => {
        fetchStatistics();
    }, [date, limit]);

    const fetchStatistics = async () => {
        setLoading(true);
        try {
            const { data, error } = await driverStatisticsApi(date, limit);
            if (data && data.data) {
                setStatistics(data.data);
                setResponseDate(data.date || moment().format("YYYY-MM-DD"));
            } else {
                setStatistics(null);
                if (error) {
                    // Handle specific error cases
                    if (error.status === 404) {
                        console.error("Endpoint not found (404):", error);
                        showError("Driver rankings endpoint not found. The API may not be implemented yet.");
                    } else if (error.status === 500) {
                        console.error("Server error (500):", error);
                        showError(`Server error: ${error.message || "Internal server error. Please try again later."}`);
                    } else if (error.message?.includes("CORS") || error.message?.includes("Cross-Origin")) {
                        console.error("CORS error:", error);
                        showError("CORS error: The backend needs to configure CORS headers for this endpoint.");
                    } else {
                        console.error("Error fetching driver statistics:", error);
                        showError(error.message || "Failed to fetch driver statistics");
                    }
                }
            }
        } catch (err) {
            console.error("Exception fetching driver statistics:", err);
            setStatistics(null);
            showError("An unexpected error occurred while fetching driver statistics");
        } finally {
            setLoading(false);
        }
    };

    return { statistics, loading, refetch: fetchStatistics, date: responseDate };
}

